import * as z from 'zod';
import {
  defineSchema,
  toStandardSchema,
  type InferSchema,
} from '@liveschema/core';
import { PaymentMethod, SeminarType } from '@/graphql/codegen/graphql';
import { customEmailSchema } from '@/utils/zod/email';
import { InvoiceTypeOptions } from '@/components/form/RadioButtonOption';

export const branchOffices = [
  'Jihlava',
  'Havlíčkův Brod',
  'Pelhřimov',
  'Žďár nad Sázavou',
  'Třebíč',
] as const;

const icoSchema = z
  .string()
  .length(8, 'Zadejte osmimístné číslo')
  .regex(/^\d+$/, 'IČO může obsahovat pouze čísla');

const invoiceSchoolSchema = z.string().min(1, 'Zadejte název a adresu školy');

export const form = defineSchema()
  .field('seminarType', z.nativeEnum(SeminarType))
  .field('isForFree', z.boolean())
  .field(
    'name',
    z
      .string()
      .min(1, 'Zadejte jméno')
      .max(40, 'Jméno nesmí být delší než 40 znaků'),
  )
  .when({ seminarType: SeminarType.Studium }, (b) =>
    b.field('address', z.string().min(1, 'Zadejte adresu')),
  )
  .field('email', customEmailSchema)
  .field('dateOfBirth', z.string().min(8, 'Zadejte datum narození'))
  .when({ seminarType: SeminarType.Studium }, (b) =>
    b
      .field('placeOfBirth', z.string().min(1, 'Zadejte město narození'))
      .field('education', z.string().min(1, 'Zadejte vzdělání'))
      .field('fileUrls', z.array(z.string()).optional()),
  )
  .field('phone', z.string().min(9, 'Zadejte platné telefonní číslo'))
  .when(
    (v) =>
      !v.isForFree &&
      (v.seminarType === SeminarType.Default ||
        v.seminarType === SeminarType.Studium),
    (b) =>
      b.field(
        'paymentMethod',
        z.enum([PaymentMethod.Invoice], {
          errorMap: () => ({ message: 'Vyberte typ platby' }),
        }),
      ),
  )
  .whenAny(
    [
      { seminarType: SeminarType.Default },
      { seminarType: SeminarType.Studium },
    ],
    (b) =>
      b
        .field(
          'invoiceType',
          z.nativeEnum(InvoiceTypeOptions, {
            errorMap: () => ({ message: 'Vyberte typ fakturace' }),
          }),
        )
        .when({ invoiceType: InvoiceTypeOptions.SCHOOL }, (b2) =>
          b2
            .field('invoiceSchool', invoiceSchoolSchema)
            .field('invoiceIco', icoSchema)
            .when(
              (v) => !v.isForFree && v.paymentMethod === PaymentMethod.Invoice,
              (b3) =>
                b3.field('invoiceEmail', z.string().min(1, 'Email je povinný')),
            ),
        ),
  )
  .whenAny(
    [{ seminarType: SeminarType.Idz }, { seminarType: SeminarType.Digcompedu }],
    (b) =>
      b
        .field('invoiceSchool', invoiceSchoolSchema)
        .when({ seminarType: SeminarType.Digcompedu }, (b2) =>
          b2.field('invoiceIco', icoSchema),
        ),
  )
  .when({ seminarType: SeminarType.Studium }, (b) =>
    b
      .field('wantsAllowance', z.boolean().optional())
      .when({ wantsAllowance: true }, (b2) =>
        b2.field('branchOffice', z.string().min(1, 'Zadejte město')),
      ),
  )
  .field('note', z.string().optional())
  .field(
    'consent',
    z.literal(true, {
      errorMap: () => ({
        message: 'Potvrďte prosím souhlas s podmínky registrace',
      }),
    }),
  );

export type RegisterFormSchema = InferSchema<typeof form>;

export const formStandardSchema = toStandardSchema<
  RegisterFormSchema,
  RegisterFormSchema
>(form);
